import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createPhoneCallFeatureLayer } from '../helpers/feature-layers';
import { createPhoneCallGraphic } from '../helpers/graphics';
import type { PhoneCallGeoPointEntity } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { areLatLngsEqual } from '../../location/helpers';
import { EsriMapEntityTypeEnum } from '../types';
import { useFeatureLayerVisibility } from './use-feature-layer-visibility';

// Phone Call (ANI/ALI)

type PhoneCallFeatureLayerProps = {
    phoneCallGeoPointEntity?: PhoneCallGeoPointEntity;
    selectedPhoneCallId?: number;
    areRapidSOSAniAliLayersVisible: boolean;
    webMap?: WebMap;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const usePhoneCallFeatureLayer = ({
    phoneCallGeoPointEntity,
    selectedPhoneCallId,
    areRapidSOSAniAliLayersVisible,
    webMap,
    selectedMapLedgerEntityType,
}: PhoneCallFeatureLayerProps) => {
    // There will be only one PhoneCall in phoneCallFeatureLayer.
    const phoneCallEntities: PhoneCallGeoPointEntity[] = phoneCallGeoPointEntity
        ? [phoneCallGeoPointEntity]
        : [];
    const [phoneCallLayer, setPhoneCallLayer] = useState<FeatureLayer>();
    const prevSelectedPhoneCallId = usePreviousLayer(selectedPhoneCallId, phoneCallLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        phoneCallLayer
    );

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.PHONE_CALLS>(
        EntityIdToObjectIdMaps.PHONE_CALLS
    );

    const didPhoneCallGraphicChange = useCallback(
        (currCall: PhoneCallGeoPointEntity, prevCall: PhoneCallGeoPointEntity) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.PHONE_CALL ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.PHONE_CALL);

            return (
                isEntityTypeChanged ||
                !areLatLngsEqual(currCall, prevCall) ||
                selectedPhoneCallId !== prevSelectedPhoneCallId
            );
        },
        [
            prevSelectedPhoneCallId,
            selectedPhoneCallId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createPhoneCallGraphics = useCallback(
        (phoneCallEntities: PhoneCallGeoPointEntity[]): Graphic[] => {
            const phoneCallGraphics: Graphic[] = [];
            for (const phoneCallGeoPointEntity of phoneCallEntities) {
                const objectId =
                    entityIdToObjectIdMapCacheRef.current.get(phoneCallGeoPointEntity.entityId) ||
                    1; // 1 set for the case, when phone call should be selected automatically in event creation mode, in that case cache does not contain values

                const phoneCallGraphic = createPhoneCallGraphic({
                    objectId,
                    phoneCallGeoPointEntity,
                    selectedPhoneCallId,
                });
                if (phoneCallGraphic) {
                    phoneCallGraphics.push(phoneCallGraphic);
                }
            }
            return phoneCallGraphics;
        },
        [selectedPhoneCallId]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const phoneCallFeatureLayer = createPhoneCallFeatureLayer();
            setPhoneCallLayer(phoneCallFeatureLayer);
            webMap.layers.add(phoneCallFeatureLayer);
        }
    }, [webMap]);

    useFeatureLayerVisibility({
        featureLayer: phoneCallLayer,
        isVisible: areRapidSOSAniAliLayersVisible,
    });

    useLayerControl<PhoneCallGeoPointEntity>({
        layer: phoneCallLayer,
        currItems: phoneCallEntities,
        createGraphics: createPhoneCallGraphics,
        didItemChange: didPhoneCallGraphicChange,
        entityIdToObjectIdMapCacheRef,
    });

    return { phoneCallLayer };
};
